
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Flag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { sanitizeInput, sanitizeUrl, validateReportReason, validateReportDescription } from "@/utils/inputValidation";

interface ReportImageButtonProps {
  photoUrl: string;
  userId: string;
}

const ReportImageButton = ({ photoUrl, userId }: ReportImageButtonProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reportReasons = [
    "Inappropriate/explicit content",
    "Nudity or sexual content",
    "Violence or disturbing content",
    "Fake or misleading profile",
    "Harassment or hate speech",
    "Copyright violation",
    "Spam or commercial content",
    "Other"
  ];

  const handleReasonChange = (reason: string, checked: boolean) => {
    if (checked) {
      setSelectedReasons(prev => [...prev, reason]);
    } else {
      setSelectedReasons(prev => prev.filter(r => r !== reason));
    }
  };

  const handleSubmitReport = async () => {
    if (!user || selectedReasons.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one reason for reporting.",
        variant: "destructive",
      });
      return;
    }

    // Enhanced input validation and sanitization
    const sanitizedReasons = selectedReasons.map(reason => sanitizeInput(reason));
    const sanitizedDescription = sanitizeInput(description);
    const sanitizedPhotoUrl = sanitizeUrl(photoUrl);

    // Validate inputs
    const reasonValidation = validateReportReason(sanitizedReasons.join(', '));
    if (!reasonValidation.isValid) {
      toast({
        title: "Invalid input",
        description: reasonValidation.error,
        variant: "destructive",
      });
      return;
    }

    const descriptionValidation = validateReportDescription(sanitizedDescription);
    if (!descriptionValidation.isValid) {
      toast({
        title: "Invalid input",
        description: descriptionValidation.error,
        variant: "destructive",
      });
      return;
    }

    if (!sanitizedPhotoUrl) {
      toast({
        title: "Error",
        description: "Invalid photo URL provided.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      // Submit report with sanitized data
      const { error } = await supabase
        .from('user_reports')
        .insert({
          reporter_id: user.id,
          reported_id: userId,
          reason: sanitizedReasons.join(', '),
          description: `Image report: ${sanitizedDescription}`,
          reported_content_type: 'image',
          reported_content_url: sanitizedPhotoUrl
        });

      if (error) throw error;

      toast({
        title: "Report submitted",
        description: "Thank you for your report. We'll review it shortly.",
      });

      setOpen(false);
      setSelectedReasons([]);
      setDescription("");
    } catch (error: any) {
      console.error('Report submission error:', error);
      toast({
        title: "Error",
        description: "Failed to submit report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-600 hover:bg-red-50"
        >
          <Flag className="w-4 h-4 mr-1" />
          Report
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report Image</DialogTitle>
          <DialogDescription>
            Help us keep the community safe by reporting inappropriate content.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Reason for reporting:</Label>
            <div className="mt-2 space-y-2">
              {reportReasons.map((reason) => (
                <div key={reason} className="flex items-center space-x-2">
                  <Checkbox
                    id={reason}
                    checked={selectedReasons.includes(reason)}
                    onCheckedChange={(checked) => handleReasonChange(reason, checked as boolean)}
                  />
                  <Label htmlFor={reason} className="text-sm">
                    {reason}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <Label htmlFor="description" className="text-sm font-medium">
              Additional details (optional):
            </Label>
            <Textarea
              id="description"
              placeholder="Provide any additional context..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
              maxLength={1000}
            />
            <div className="text-xs text-gray-500 mt-1">
              {description.length}/1000 characters
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReport}
              disabled={submitting || selectedReasons.length === 0}
              className="bg-red-500 hover:bg-red-600"
            >
              {submitting ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportImageButton;
